import { TestResult, TestStep } from "../types";
import {
  ScriptTestResultDetail,
  convertToJST as _convertToJST,
  getStatusDisplayName,
  Template,
  TestStepStatusIds,
  Issue,
  ScriptTestResultsIssue,
  BuildVersion,
} from "../services/api";

// convertToJST関数を再エクスポート
export const convertToJST = _convertToJST;

// API結果用の型定義
export interface ApiTestResult {
  testRunMetaId: string;
  testRunName: string;
  testSuiteMetaId: string;
  testSuiteName: string;
  testCaseMetaId: string;
  testCaseName: string;
  workspace: string;
  project: string;
  url: string;
  testCaseDetail: TestCaseValue;
  scriptTestRunResult: TestResultData;
  testRunId: string;
  operationLog: any;
  isProcessed?: boolean;
  status?: string;
  environment: string;
  executionDateTime: string;
  issues?: Issue[];
  scriptTestResultsIssues?: ScriptTestResultsIssue[];
  buildVersion?: BuildVersion;
}

export interface TestCaseValue {
  workspace_id: string;
  project_id: string;
  testcase_id: string;
  meta_id: string;
  name: string;
  display_order: number;
  value: string; // JSON文字列
  teststep?: TestStepData[];
}

export interface TestResultData {
  result_id: string;
  script_test_run_matrix_id: string;
  test_result_status_id: string;
  modified_at: string;
  comment: string;
  value: string; // JSON文字列
  managed_file_list?: any[];
  test_step_result?: TestStepResultData[];
}

export interface TestStepData {
  id: string;
  step: string;
  expect: string;
  action_detail?: any;
}

export interface TestStepResultData {
  test_step_id: string;
  test_result_status_id: string;
  score?: number;
  reason?: string;
  actual_step?: string;
  score_reason?: string;
}

// 検索・フィルタリング用の型
export interface SearchFilters {
  workspace: string;
  project: string;
  testRunId: string;
  searchType: "analyzer_waiting" | "interrupted" | "all" | "needs_review";
  /** true のとき、人間の判定と AI の判定（tag_analysis）が異なる行のみ表示 */
  judgmentMismatchOnly?: boolean;
  query?: any; // 追加のクエリパラメータ
  /**
   * dedup 後も必ず保持したい script_test_run_result.result_id のリスト。
   * 同 (test_run, testcase, env) の古い再実行 result は通常 dedup で消えるが、
   * ダイレクトモードで URL から指定された result はリグレッション履歴比較
   * のため明示的に保持する必要があるため利用する。
   */
  keepResultIds?: string[];
}

/**
 * 同一 (test_run, testcase, execution_environment) で modified_at が最新の
 * result のみを残す。ただし `keepResultIds` に含まれる result は古くても残す
 * （リグレッションテストの履歴比較で過去 result を消さないため）。
 */
export function deduplicateLatestResults(
  results: any[],
  keepResultIds?: string[]
): any[] {
  const keepSet = new Set(keepResultIds ?? []);
  const latestResults = new Map<string, any>();
  for (const result of results) {
    const key = `${result.test_run.test_run_id}_${result.test_run_test_case.testcase_id}_${result.test_run_execution_environment.execution_environment_id}`;
    const existing = latestResults.get(key);
    if (
      !existing ||
      result.script_test_run_result.modified_at >
        existing.script_test_run_result.modified_at
    ) {
      latestResults.set(key, result);
    }
  }
  const latestArray = Array.from(latestResults.values());
  if (keepSet.size === 0) {
    return latestArray;
  }
  const latestIds = new Set(
    latestArray.map((r) => r.script_test_run_result.result_id)
  );
  const additional = results.filter(
    (r) =>
      keepSet.has(r.script_test_run_result.result_id) &&
      !latestIds.has(r.script_test_run_result.result_id)
  );
  return [...latestArray, ...additional];
}

/**
 * ダイレクトモードで開いた result が属するシナリオグループ（同 test_suite ×
 * 同 execution_environment）だけに表示を絞り込むためのフィルタ。
 *
 * `filter` が null/未指定の場合は何もせず全件返す。
 * ユーザーが手動検索を行ったタイミング等、ダイレクトモード由来の絞り込みが
 * 不要になったときは呼び出し側で filter を null にして渡すこと（さもないと
 * 新しい検索結果が古いダイレクトモードのスコープで弾かれて空表示になる）。
 */
export interface ScenarioGroupFilter {
  testSuiteMetaId: string;
  executionEnvironmentName: string;
}

export function applyScenarioGroupFilter(
  results: ApiTestResult[],
  filter: ScenarioGroupFilter | null | undefined
): ApiTestResult[] {
  if (!filter) return results;
  return results.filter(
    (r) =>
      r.testSuiteMetaId === filter.testSuiteMetaId &&
      r.environment === filter.executionEnvironmentName
  );
}

export interface SearchResult {
  testResults: ApiTestResult[];
  total: number;
}

/**
 * 結果データからステップ定義の復元元（testCaseValue）を解決する。
 *
 * 結果 value 内の `testcase_snapshot`（reimport 等でケースが変わっても紐付く凍結）に
 * 有効な `teststep` 配列があればそれを優先する。無い場合のみ live のテストケース value に
 * フォールバックし、その際 warn ログを出す。
 *
 * 背景: ai_sstf_controller#807 / PR#808 以降、全結果経路で結果 value に derived を含む
 * `testcase_snapshot` が常時格納される。フォールバック経路は PR#808 以前に作られた
 * 過去結果のための経過措置であり、凍結廃止（PUBLISH_FREEZE_TESTSTEP=false）後も
 * 新規結果は snapshot から復元されるため表示は壊れない。
 *
 * 撤去条件（#886 で方針(A)＝移行期はフォールバック維持＋warn を採用）:
 * 2026-07-22（ai_sstf_controller PR#808 マージ）より前に作られた結果が
 * 参照されなくなり、本 warn がブラウザコンソール／運用ログで観測されなくなった時点で
 * この関数からフォールバック（`getLiveValue` 引数ごと）を撤去し snapshot 一本化する。
 * 呼び出し側（dataTransform 2 経路・TagInfoButton・TagDetailModal）は
 * すべて本関数経由のため、撤去はここ 1 箇所の変更で完結する。
 *
 * @param resultData 結果 value をパースしたオブジェクト（testcase_snapshot を含みうる）
 * @param getLiveValue フォールバック時のみ遅延評価される live テストケース value の取得関数
 * @param context ログ用の呼び出し元識別子
 */
export function resolveTestCaseValue(
  resultData: any,
  getLiveValue: () => any,
  context?: string
): any {
  const snapshot = resultData?.testcase_snapshot;
  if (snapshot && Array.isArray(snapshot.teststep)) {
    return snapshot;
  }
  console.warn(
    `[testcase_snapshot fallback] 有効な testcase_snapshot.teststep が無いため live value.teststep を使用します${
      context ? ` (${context})` : ""
    }`
  );
  return getLiveValue() ?? {};
}

// 必要な情報が設定されているかチェックする関数
function hasRequiredTestStepData(result: ScriptTestResultDetail): boolean {
  try {
    // valueが存在しない、または"undefined"文字列の場合はスキップ
    if (
      !result.script_test_run_result.value ||
      result.script_test_run_result.value === "undefined" ||
      result.script_test_run_result.value === "null"
    ) {
      console.log(
        "⚠️ valueが無効です（データは表示します）:",
        result.script_test_run_result.result_id,
        result.script_test_run_result.value
      );
      return true; // データは表示する
    }

    // テスト結果のvalueをパース
    const testResultData = JSON.parse(result.script_test_run_result.value);

    // test_step_resultが存在するかチェック
    if (
      !testResultData.test_step_result ||
      !Array.isArray(testResultData.test_step_result)
    ) {
      console.log(
        "⚠️ test_step_resultが存在しません（データは表示します）:",
        result.script_test_run_result.result_id
      );
      // test_step_resultがなくても表示するように変更
      return true;
    }

    // test_step_resultが空の場合も表示する
    if (testResultData.test_step_result.length === 0) {
      console.log(
        "⚠️ test_step_resultが空です（データは表示します）:",
        result.script_test_run_result.result_id
      );
      // test_step_resultが空でも表示するように変更
      return true;
    }

    // テストケースのステップ定義を取得（結果にスナップショットがあれば優先：reimport等でケースが変わっても紐付け可能）
    let testCaseSteps: any[] = [];
    try {
      const testCaseValue = resolveTestCaseValue(
        testResultData,
        () => JSON.parse(result.test_run_test_case.value),
        "hasRequiredTestStepData"
      );
      testCaseSteps = testCaseValue.teststep || [];
    } catch (error) {
      console.log(
        "❌ テストケース定義の解析エラー:",
        result.script_test_run_result.result_id,
        error
      );
      return false;
    }

    // テストケースにステップ定義がない場合でも、test_step_resultがあれば処理を続行
    if (testCaseSteps.length === 0) {
      console.log(
        "⚠️ テストケースにステップ定義がありませんが、処理を続行します:",
        result.script_test_run_result.result_id
      );
      // test_step_resultがある場合は、有効なデータがあるかチェック
      if (
        testResultData.test_step_result &&
        testResultData.test_step_result.length > 0
      ) {
        const validStepsCount = testResultData.test_step_result.filter(
          (stepResult: any) =>
            stepResult.score !== undefined &&
            stepResult.score !== null &&
            stepResult.score_reason &&
            stepResult.score_reason.trim() !== ""
        ).length;

        if (validStepsCount === 0) {
          console.log(
            "⚠️ 有効なステップデータ（score, score_reason）が存在しません（データは表示します）:",
            result.script_test_run_result.result_id
          );
          // 有効なステップデータがなくても表示する
          return true;
        }

        console.log(
          `✅ test_step_resultから有効データを検出: ${validStepsCount}件`,
          result.script_test_run_result.result_id
        );
      }
      return true;
    }

    // 実施済みステップの数をカウント
    let implementedStepsCount = 0;
    let validStepsCount = 0;

    testCaseSteps.forEach((step: any) => {
      // 対応する結果を検索
      const stepResult = testResultData.test_step_result.find(
        (r: any) => r.test_step_id === step.id
      );

      if (stepResult) {
        implementedStepsCount++;
        // 有効なデータ（score, score_reason）があるかチェック
        if (
          stepResult.score !== undefined &&
          stepResult.score !== null &&
          stepResult.score_reason &&
          stepResult.score_reason.trim() !== ""
        ) {
          validStepsCount++;
        }
      }
    });

    console.log(
      `ステップ状況 - 全体:${testCaseSteps.length}, 実施済み:${implementedStepsCount}, 有効データ:${validStepsCount}`,
      result.script_test_run_result.result_id
    );

    // 実施済みステップが1つもない場合でも表示する
    if (implementedStepsCount === 0) {
      console.log(
        "⚠️ 実施済みステップが1つもありません（データは表示します）:",
        result.script_test_run_result.result_id
      );
      return true;
    }

    // 有効なデータを持つステップが1つもない場合でも表示する
    if (validStepsCount === 0) {
      console.log(
        "⚠️ 有効なステップデータ（score, score_reason）が存在しません（データは表示します）:",
        result.script_test_run_result.result_id
      );
      return true;
    }

    console.log(
      "✅ 必要なデータが揃っています:",
      result.script_test_run_result.result_id
    );
    return true;
  } catch (error) {
    console.log(
      "⚠️ データ解析エラー（データは表示します）:",
      result.script_test_run_result.result_id,
      error
    );
    // エラーが発生してもデータは表示する
    return true;
  }
}

// APIレスポンスからReactアプリ用のデータに変換
export function transformApiTestResults(
  apiResults: ScriptTestResultDetail[],
  workspaceSlugMap: Record<string, string>,
  projectSlugMap: Record<string, string>,
  sstfBaseUrl: string,
  template?: Template
): ApiTestResult[] {
  console.log("transformApiTestResults開始:", apiResults.length, "件のデータ");
  console.log("最初のAPIレスポンス:", apiResults[0]);

  // 必要な情報が設定されているデータのみをフィルタリング
  const filteredResults = apiResults.filter(hasRequiredTestStepData);
  console.log(
    `フィルタリング結果: ${filteredResults.length}/${apiResults.length} 件のデータが条件を満たしています`
  );

  return filteredResults.map((result) => {
    const workspaceSlug =
      workspaceSlugMap[result.test_run.workspace_id] || "unknown";
    const projectSlug = projectSlugMap[result.test_run.project_id] || "unknown";
    const url = `${sstfBaseUrl}/${workspaceSlug}/${projectSlug}/testruns/${result.test_run.test_run_id}/execution?t=d_testcase&k=${result.test_run_test_case.testcase_id}&dm=d&d_tab=`;

    // ステータス表示名を取得
    const status = template
      ? getStatusDisplayName(
          template,
          result.script_test_run_result.test_result_status_id
        )
      : "未実施";

    const transformedResult: ApiTestResult = {
      testRunMetaId: result.test_run.meta_id,
      testRunName: result.test_run.name,
      testSuiteMetaId: result.test_run_test_suite.meta_id,
      testSuiteName: result.test_run_test_suite.name,
      testCaseName: result.test_run_test_case.name,
      testCaseMetaId: result.test_run_test_case.meta_id,
      environment: result.test_run_execution_environment.name,
      executionDateTime: _convertToJST(
        result.script_test_run_result.modified_at
      ),
      status: status,
      workspace: workspaceSlug,
      project: projectSlug,
      url,
      testCaseDetail: {
        workspace_id: result.test_run.workspace_id,
        project_id: result.test_run.project_id,
        testcase_id: result.test_run_test_case.testcase_id,
        meta_id: result.test_run_test_case.meta_id,
        name: result.test_run_test_case.name,
        value: result.test_run_test_case.value,
        display_order: result.test_run_test_case.display_order,
      },
      scriptTestRunResult: {
        result_id: result.script_test_run_result.result_id,
        script_test_run_matrix_id:
          result.script_test_run_result.script_test_run_matrix_id,
        test_result_status_id:
          result.script_test_run_result.test_result_status_id,
        modified_at: result.script_test_run_result.modified_at,
        comment: result.script_test_run_result.comment,
        value: result.script_test_run_result.value,
        managed_file_list: result.script_test_run_result.managed_file_list,
      },
      testRunId: result.test_run.test_run_id,
      operationLog: null,
      isProcessed: false,
      // 課題データを保持
      issues: result.issues,
      scriptTestResultsIssues: result.script_test_results_issues,
      buildVersion: result.build_version,
    };

    return transformedResult;
  });
}

// APIのテスト結果を内部形式に変換
export function convertApiTestResultToTestResult(
  apiTestResult: ApiTestResult,
  template?: Template,
  testStepStatusIds?: TestStepStatusIds
): TestResult {
  try {
    // テストケース詳細と結果をパース
    let testCaseValue: any = {};
    let testResultData: any = {};

    try {
      // valueが存在し、有効な場合のみパース
      if (apiTestResult.scriptTestRunResult.value &&
          apiTestResult.scriptTestRunResult.value !== "undefined" &&
          apiTestResult.scriptTestRunResult.value !== "null") {
        testResultData = JSON.parse(apiTestResult.scriptTestRunResult.value);
      }
    } catch (e) {
      console.warn("テスト結果のパースに失敗:", e);
    }

    try {
      // 結果にスナップショットがあればスナップショットを優先（reimport等でケース変更後も正表示）。
      // 無い場合のみ live のテストケース詳細へフォールバック（resolveTestCaseValue 内で warn）。
      testCaseValue = resolveTestCaseValue(
        testResultData,
        () => JSON.parse(apiTestResult.testCaseDetail.value),
        "convertApiTestResultToTestResult"
      );
    } catch (e) {
      console.warn("テストケース詳細のパースに失敗:", e);
    }

    // テストケース構造のデバッグログ追加
    console.log("📋 テストケース構造確認:", {
      hasTestCaseValue: !!testCaseValue,
      testCaseValueType: typeof testCaseValue,
      testCaseValueKeys: testCaseValue ? Object.keys(testCaseValue) : null,
      testCaseValue: testCaseValue,
      verification_procedure: testCaseValue?.verification_procedure,
      expected_result: testCaseValue?.expected_result,
      teststep: testCaseValue?.teststep,
      testStepLength: testCaseValue?.teststep?.length || 0,
      testResultLength: testResultData?.test_step_result?.length || 0,
    });

    const steps = convertTestSteps(
      testCaseValue.teststep || [],
      testResultData.test_step_result || [],
      testStepStatusIds,
      testCaseValue // testCaseValueを追加で渡す
    );

    // 実行時間を計算
    const startTime = new Date(apiTestResult.executionDateTime).getTime();
    const endTime = startTime + (steps.length > 0 ? 300000 : 0); // 5分間のデフォルト

    // ステータスを判定
    const status = steps.length > 0 ? determineOverallStatus(steps) : "pending";

    return {
      id: apiTestResult.scriptTestRunResult.result_id,
      name: apiTestResult.testCaseDetail.name,
      metaId: apiTestResult.testCaseDetail.meta_id,
      status,
      startTime,
      endTime,
      executionTime: endTime - startTime,
      executionMode: "auto",
      comment: apiTestResult.scriptTestRunResult.comment,
      steps,
      videos: [],
      traceId: apiTestResult.testRunId,
      environment: apiTestResult.environment,
      value: testResultData, // valueプロパティを追加
    };
  } catch (error) {
    console.error("convertApiTestResultToTestResult エラー:", error);
    // エラー時のフォールバック
    // valueをパースしようと試みる
    let fallbackValue = {};
    try {
      fallbackValue = JSON.parse(apiTestResult.scriptTestRunResult.value);
    } catch (e) {
      // パースできない場合は空オブジェクト
    }

    return {
      id: apiTestResult.scriptTestRunResult.result_id,
      name: apiTestResult.testCaseDetail.name,
      metaId: apiTestResult.testCaseDetail.meta_id,
      status: "pending",
      startTime: new Date(apiTestResult.executionDateTime).getTime(),
      endTime: new Date(apiTestResult.executionDateTime).getTime(),
      executionTime: 0,
      executionMode: "auto",
      comment: apiTestResult.scriptTestRunResult.comment,
      steps: [],
      videos: [],
      traceId: apiTestResult.testRunId,
      environment: apiTestResult.environment,
      value: fallbackValue, // valueプロパティを追加
    };
  }
}

// テストケース情報からステップを作成する共通関数
function createStepFromTestCaseInfo(
  testCaseValue: any,
  baseId: string,
  baseTime: number,
  result?: any,
  testStepStatusIds?: TestStepStatusIds
): TestStep {
  // テストケース情報があればそれを使用、なければデフォルト
  const label = testCaseValue?.verification_procedure || "ステップ定義なし";
  const action =
    testCaseValue?.verification_procedure || "実施手順不明（ステップ定義なし）";
  const description =
    testCaseValue?.expected_result || "詳細情報なし（ステップ定義なし）";

  // test_step_resultがある場合は成功判定を行う
  const stepResult = result
    ? isStepSuccessful(null, result, testStepStatusIds)
    : "not_evaluated";

  const scoreReason = result
    ? result.score_reason
    : testCaseValue?.verification_procedure
    ? "未評価"
    : "ステップ定義がないため評価できません";

  return {
    id: baseId,
    label,
    action,
    result: stepResult,
    startTime: baseTime,
    endTime: baseTime + 3000,
    evidence: null,
    params: {},
    item: {
      name: label,
      description,
    },
    score: result?.score,
    reason: result?.reason || "未評価",
    actualStep: result?.actual_step || "実施内容不明",
    scoreReason,
    statusId: result?.test_result_status_id,
    statusDisplayName: undefined,
  };
}

// テストステップを変換
function convertTestSteps(
  testSteps: any[],
  testResults: any[],
  testStepStatusIds?: TestStepStatusIds,
  testCaseValue?: any
): TestStep[] {
  // テストケース定義のステップが存在し、かつ有効な内容がある場合のみ従来通りの処理
  const hasValidTestSteps =
    testSteps.length > 0 &&
    testSteps.some(
      (step) =>
        step.step && typeof step.step === "string" && step.step.trim() !== ""
    );

  if (hasValidTestSteps) {
    console.log("✅ 有効なテストケース定義が存在するため従来通りの処理を実行");
    return testSteps.map((step, index) => {
      // 対応する結果を検索
      const result = testResults.find((r) => r.test_step_id === step.id);

      // 仮の時間を設定
      const baseTime = Date.now();
      const startTime = baseTime + index * 5000; // 5秒間隔
      const endTime = startTime + 3000; // 3秒実行

      // teststep.order はシナリオグループ内 TC ではグループ通し番号 (例: 2 番目 TC で 3,4)。
      // リグレッション画像名 (`expected_step_N.jpg`) と `regression_baseline` のキーは
      // teststep.order で揃っているため、位置 index ではなく原本の order を後段に伝える。
      const stepOrder =
        typeof step.order === "number"
          ? step.order
          : typeof step.order === "string" && step.order.trim() !== ""
          ? Number(step.order)
          : undefined;

      if (result) {
        console.log("✅ ステップ結果あり:", step.id, result);
        // ステップの成功判定
        const isSuccessful = isStepSuccessful(step, result, testStepStatusIds);

        return {
          id: step.id,
          label: step.step,
          action: step.step,
          result: isSuccessful,
          startTime,
          endTime,
          evidence: null,
          params: {},
          item: {
            name: step.step,
            description: step.expect || "",
          },
          score: result.score,
          reason: result.reason,
          actualStep: result.actual_step || "",
          scoreReason: result.score_reason,
          statusId: result.test_result_status_id, // ステータスIDを保存
          statusDisplayName: undefined, // TODO: テンプレートから表示名を取得
          order: stepOrder,
        };
      } else {
        console.log("⚠️ ステップ結果なし（未評価）:", step.id);
        // 未評価ステップとして扱う
        return {
          id: step.id,
          label: step.step,
          action: step.step,
          result: "not_evaluated", // 未評価として扱う
          startTime,
          endTime,
          evidence: null,
          params: {},
          item: {
            name: step.step,
            description: step.expect || "",
          },
          score: undefined,
          reason: "未評価",
          actualStep: "未評価",
          scoreReason: "このステップはまだ評価されていません",
          order: stepOrder,
        };
      }
    });
  }

  // test_step_resultがない場合、テストケース情報を使用してステップを作成
  if (!testResults || testResults.length === 0) {
    console.log(
      "⚠️ test_step_resultがないため、テストケース情報を使用してステップを作成します",
      {
        testResults,
        testResultsLength: testResults?.length || 0,
        hasTestCaseValue: !!testCaseValue,
        verification_procedure: testCaseValue?.verification_procedure,
        expected_result: testCaseValue?.expected_result,
      }
    );

    const baseTime = Date.now();
    const stepId = testCaseValue?.verification_procedure
      ? "testcase-based-step"
      : "default-step-no-definition";

    // 共通関数を使用してステップを作成
    const step = createStepFromTestCaseInfo(testCaseValue, stepId, baseTime);

    console.log("✅ ステップを作成しました:", step);
    return [step];
  }

  // テストケース定義がない、または無効な場合は、test_step_resultを基準にステップを生成
  console.log(
    "📝 テストケース定義が無効または存在しないため、test_step_resultからステップを生成します"
  );

  return testResults.map((result, index) => {
    // 仮の時間を設定
    const baseTime = Date.now();
    const startTime = baseTime + index * 5000; // 5秒間隔

    console.log(
      "✅ test_step_resultからステップを生成:",
      result.test_step_id,
      result
    );

    // 共通関数を使用してステップを作成（resultとtestStepStatusIdsを渡す）
    return createStepFromTestCaseInfo(
      testCaseValue,
      result.test_step_id,
      startTime,
      result,
      testStepStatusIds
    );
  });
}

// 全体のステータスを判定
function determineOverallStatus(steps: TestStep[]): TestResult["status"] {
  if (steps.length === 0) return "pending";

  const failedSteps = steps.filter((step) => step.result === "failure");
  const warningSteps = steps.filter((step) => step.result === "warning");
  const notEvaluatedSteps = steps.filter(
    (step) => step.result === "not_evaluated"
  );
  const successSteps = steps.filter((step) => step.result === "success");

  if (failedSteps.length > 0) {
    return "step_failed";
  } else if (notEvaluatedSteps.length > 0 && successSteps.length === 0) {
    // 未評価のステップがあり、成功したステップがない場合は pending
    return "pending";
  } else if (warningSteps.length > 0) {
    // 警告があるが失敗はない場合は成功とする（警告は個別ステップで表示）
    return "success";
  } else {
    return "success";
  }
}

// テストステップが成功かどうかを判定
function isStepSuccessful(
  _step: any,
  result: any,
  testStepStatusIds: any
): "success" | "warning" | "failure" | "not_evaluated" {
  // resultが存在しない場合は未評価
  if (!result) {
    return "not_evaluated";
  }

  // SUCCESS_IDS配列が利用可能な場合は、それを使用
  if (
    testStepStatusIds?.SUCCESS_IDS &&
    testStepStatusIds.SUCCESS_IDS.length > 0
  ) {
    const isSuccessById = testStepStatusIds.SUCCESS_IDS.includes(
      result?.test_result_status_id
    );

    console.log("isSuccessById:", testStepStatusIds.SUCCESS_IDS, result);

    if (isSuccessById) {
      return "success";
    }
  }

  // WARNING_IDS配列による判定
  if (
    testStepStatusIds?.WARNING_IDS &&
    testStepStatusIds.WARNING_IDS.length > 0
  ) {
    const isWarningById = testStepStatusIds.WARNING_IDS.includes(
      result?.test_result_status_id
    );

    if (isWarningById) {
      return "warning";
    }
  }

  // result.resultフィールドがtrueの場合も成功とする
  if (result?.result === true) {
    return "success";
  }

  return "failure";
}

// 統計情報を計算
export function calculateTestResultStats(results: ApiTestResult[]): {
  total: number;
  completed: number;
  statusCounts: Record<string, number>;
} {
  const statusCounts: Record<string, number> = {};
  let completed = 0;

  results.forEach((result) => {
    // ステータスをカウント
    const status = result.status || "未実施";
    statusCounts[status] = (statusCounts[status] || 0) + 1;

    // 完了済みをカウント
    if (result.isProcessed) {
      completed++;
    }
  });

  return {
    total: results.length,
    completed,
    statusCounts,
  };
}

// 優先度に基づいてテスト結果をソート
export function sortTestResultsByPriority(
  results: ApiTestResult[]
): ApiTestResult[] {
  const statusPriority: Record<string, number> = {
    "[AI]結果NG": 1,
    "[AI]結果NG(要確認)": 2,
    "[AI]手順NG(要確認)": 3,
    "[AI]OK(要確認)": 4,
    "[AI]手順NG": 5,
    "[AI]OK": 6,
    未実施: 7, // 未実施は最低優先度
  };

  return [...results].sort((a, b) => {
    const priorityA = statusPriority[a.status || ""] || 999;
    const priorityB = statusPriority[b.status || ""] || 999;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // 優先度が同じ場合は実行日時でソート
    return (
      new Date(a.executionDateTime).getTime() -
      new Date(b.executionDateTime).getTime()
    );
  });
}

// テストラン、テストスイート、実行環境でグループ化してdisplay_order順でソート
export function sortTestResultsByDisplayOrder(
  results: ApiTestResult[]
): ApiTestResult[] {
  return [...results].sort((a, b) => {
    // 1. テストランID順
    if (a.testRunId !== b.testRunId) {
      return a.testRunId.localeCompare(b.testRunId);
    }

    // 2. テストスイートID順
    if (a.testSuiteMetaId !== b.testSuiteMetaId) {
      return a.testSuiteMetaId.localeCompare(b.testSuiteMetaId);
    }

    // 3. 実行環境ID順
    if (a.environment !== b.environment) {
      return a.environment.localeCompare(b.environment);
    }

    // 4. display_order順
    const displayOrderA = getDisplayOrder(a);
    const displayOrderB = getDisplayOrder(b);

    if (displayOrderA !== displayOrderB) {
      return displayOrderA - displayOrderB;
    }

    // 5. 最後にテストケースID順（同じdisplay_orderの場合）
    return a.testCaseMetaId.localeCompare(b.testCaseMetaId);
  });
}

// テストケースのdisplay_orderを取得
function getDisplayOrder(result: ApiTestResult): number {
  try {
    const displayOrder = result.testCaseDetail.display_order;

    // 数値に変換、失敗した場合は999999を返す
    const order =
      typeof displayOrder === "number"
        ? displayOrder
        : parseInt(displayOrder, 10);
    const finalOrder = isNaN(order) ? 999999 : order;

    console.log(
      `display_order取得: ${result.testCaseMetaId} = ${finalOrder} (元データ: ${displayOrder})`
    );
    return finalOrder;
  } catch (error) {
    console.warn("display_orderの取得に失敗:", error, result.testCaseMetaId);
    return 999999; // パースエラーの場合は最後尾にソート
  }
}

// 操作ログからビデオ情報を抽出
export function extractVideoInfo(
  operationLog: any
): { url: string; startTime: number; endTime: number }[] {
  if (!operationLog?.videos) return [];

  return operationLog.videos.map((video: any) => ({
    url: video.url || "",
    startTime: video.start_time || 0,
    endTime: video.end_time || 0,
  }));
}

// 操作ログからステップにエビデンス情報を追加
export function enrichStepsWithOperationLog(
  steps: TestStep[],
  operationLog: any
): TestStep[] {
  if (!operationLog?.steps) return steps;

  return steps.map((step, index) => {
    const logStep = operationLog.steps[index];
    if (!logStep) return step;

    // 操作ログの時間情報を使用（ミリ秒単位のテスト開始からの経過時間）
    return {
      ...step,
      startTime: logStep.start_time || step.startTime,
      endTime: logStep.end_time || step.endTime,
      evidence: logStep.evidence || step.evidence,
      params: { ...step.params, ...logStep.params },
    };
  });
}
